import React, { useEffect, useState } from 'react';

import { ActionIcon, Box, Center, Chip, Flex, Loader, Menu, Paper, ScrollArea, Select, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconCircleChevronRight,
  IconFileChart,
  IconFileTypeDoc,
  IconFileTypePdf,
  IconFolderFilled,
  IconSearch
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { GoogleDriveAccountHandler } from 'components/GoogleDriveAccountHandler/GoogleDriveAccountHandler';
import { SelectItem } from 'components/SelectItem';
import { useTranslation } from 'react-i18next';
import { GoogleDriveService } from 'services/google-drive-service';
import { GOOGLE_DRIVE_FILES_QUERY_KEY, GOOGLE_DRIVE_SEARCH_QUERY_KEY } from 'shared/constants/query-keys';
import { PRIMARY_SHADE_DARK } from 'shared/constants/theme';
import {
  GOOGLE_DRIVE_ROOT,
  GoogleDriveAccount,
  GoogleDriveFileInfo,
  GoogleDriveFilesSearchQueryResult,
  GoogleDriveMimeType
} from 'shared/interfaces/GoogleDrive';

interface GoogleDriveSelectorProps {
  onFileSelect: (file: GoogleDriveFileInfo) => void;
  isFileSelected: (fileId: string) => boolean;
}

export function GoogleDriveSelector({ onFileSelect, isFileSelected }: GoogleDriveSelectorProps) {
  const { t } = useTranslation();
  const [selectedGoogleDriveAccount, setSelectedGoogleDriveAccount] = useState<GoogleDriveAccount | undefined>(
    undefined
  );
  const [displayedFiles, setDisplayedFiles] = useState<GoogleDriveFileInfo[]>([]);
  const [searchQueryResults, setSearchQueryResults] = useState<GoogleDriveFileInfo[]>([]);
  const [currentNextPageToken, setCurrentNextPageToken] = useState<string | undefined>(undefined);
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [currentFilePath, setCurrentFilePath] = useState<GoogleDriveFileInfo[]>([]);
  const [clickTimeout, setClickTimeout] = useState<number | null>(null);

  const searchQueryForm = useForm({
    initialValues: {
      searchQuery: ''
    }
  });

  const getFilesQuery = useQuery({
    queryKey: [GOOGLE_DRIVE_FILES_QUERY_KEY, currentFolderId, selectedGoogleDriveAccount?._id ?? ''],
    queryFn: () =>
      GoogleDriveService.getFiles({
        googleDriveId: selectedGoogleDriveAccount?._id ?? '', // Should never be undefined because of the enabled check
        folderId: currentFolderId,
        pageToken: currentNextPageToken
      }),
    onSuccess: (queryResult: GoogleDriveFilesSearchQueryResult) => {
      setDisplayedFiles(queryResult.files);
      setCurrentNextPageToken(queryResult.nextPageToken);
    },
    enabled: !!selectedGoogleDriveAccount,
    cacheTime: 0
  });

  const getSearchFilesQuery = useQuery({
    queryKey: [GOOGLE_DRIVE_SEARCH_QUERY_KEY, selectedGoogleDriveAccount?._id ?? ''],
    queryFn: () =>
      GoogleDriveService.getFiles({
        googleDriveId: selectedGoogleDriveAccount?._id ?? '', // Should never be undefined because of the enabled check
        searchQuery: searchQueryForm.values.searchQuery
      }),
    onSuccess: (queryResult: GoogleDriveFilesSearchQueryResult) => {
      if (!searchQueryForm.values.searchQuery) return;
      setSearchQueryResults(queryResult.files);
    },
    enabled: false,
    cacheTime: 0
  });

  useEffect(() => {
    searchQueryForm.values.searchQuery ? getSearchFilesQuery.refetch() : setSearchQueryResults([]);
  }, [searchQueryForm.values.searchQuery]);

  const getFileIcon = (file: GoogleDriveFileInfo) => {
    switch (file.mimeType) {
      case GoogleDriveMimeType.DOCUMENT:
        return <IconFileTypeDoc color="#1a73e8" />;
      case GoogleDriveMimeType.PDF:
        return <IconFileTypePdf color="#e94335" />;
      case GoogleDriveMimeType.SLIDES:
        return <IconFileChart color="#ffba00" />;
      default:
        return <IconFolderFilled />;
    }
  };

  const onDoubleClick = (file: GoogleDriveFileInfo) => {
    if (file.mimeType !== GoogleDriveMimeType.FOLDER) return;
    setCurrentFolderId(file.id);
    setCurrentFilePath([...currentFilePath, file]);
  };

  const onClick = (file: GoogleDriveFileInfo) => onFileSelect(file);

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, file: GoogleDriveFileInfo) => {
    event.preventDefault();
    if (event.detail === 1) {
      // Set a timeout to delay the handling of the click event
      const timeout = window.setTimeout(() => {
        onClick(file);
      }, 200);
      setClickTimeout(timeout);
    } else if (event.detail === 2) {
      // Clear the timeout to cancel the click event handler
      if (clickTimeout !== null) window.clearTimeout(clickTimeout);
      onDoubleClick(file);
    }
  };

  const onFolderNavClick = (folder: GoogleDriveFileInfo) => {
    const folderIndex = currentFilePath.findIndex((path) => path.id == folder.id);
    setCurrentFilePath(currentFilePath.slice(0, folderIndex + 1));
    setCurrentFolderId(folder.id);
  };

  return selectedGoogleDriveAccount ? (
    <Box ml="auto" mr="auto" w="95%" pt={70} pb={20} h="100%">
      <Select
        mb={15}
        placeholder={t('GOOGLE_DRIVE.SEARCH_DRIVE')}
        icon={<IconSearch size={18} />}
        searchable
        rightSection={getSearchFilesQuery.isFetching ? <Loader size={15} /> : <></>}
        itemComponent={SelectItem}
        value={''}
        searchValue={searchQueryForm.values.searchQuery}
        onSearchChange={(value) => searchQueryForm.setFieldValue('searchQuery', value)}
        onChange={(file) => {
          if (!file) return;
          const fileObj = JSON.parse(file);
          onFileSelect(fileObj);
          searchQueryForm.setFieldValue('searchQuery', '');
        }}
        data={searchQueryResults.map((file) => ({
          icon: getFileIcon(file),
          label: file.name,
          value: JSON.stringify(file),
          maw: 500
        }))}
      />
      <Box h="100%">
        <Flex direction="row" w="100%">
          {currentFilePath.length > 4 ? (
            <>
              {currentFilePath.slice(0, 2).map((folder, idx) => (
                <Chip key={idx} mr={5} checked={false} onClick={() => onFolderNavClick(folder)}>
                  <Text truncate maw={100} mt={2}>
                    {folder.name}
                  </Text>
                </Chip>
              ))}

              <Menu shadow="md" width={180} position="bottom-start" offset={15}>
                <Menu.Target>
                  <Chip mr={5} checked={false}>
                    ...
                  </Chip>
                </Menu.Target>

                <Menu.Dropdown>
                  {currentFilePath.slice(2, -2).map((folder, idx) => (
                    <Menu.Item key={idx} onClick={() => onFolderNavClick(folder)}>
                      <Text truncate maw={100} mt={2}>
                        {folder.name}
                      </Text>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
              {currentFilePath.slice(-2).map((folder, idx) => (
                <Chip key={idx} mr={5} checked={false} onClick={() => onFolderNavClick(folder)}>
                  <Text truncate maw={100} mt={2}>
                    {folder.name}
                  </Text>
                </Chip>
              ))}
            </>
          ) : (
            currentFilePath.map((folder, idx) => (
              <Chip key={idx} mr={5} checked={false} onClick={() => onFolderNavClick(folder)}>
                <Text truncate maw={100} mt={2}>
                  {folder.name}
                </Text>
              </Chip>
            ))
          )}
        </Flex>
        {getFilesQuery.isLoading ? (
          <Center h="100%">
            <Loader variant="dots" />
          </Center>
        ) : (
          <ScrollArea h={560} w="100%" mt={5} offsetScrollbars>
            {displayedFiles.map((file, idx) => (
              <Paper
                withBorder
                key={`${file.name}_${idx}`}
                w="100%"
                my={7}
                p={10}
                className="g-hover-style"
                sx={(theme) => ({
                  borderColor: isFileSelected(file.id)
                    ? `${theme.colors.dark[PRIMARY_SHADE_DARK]} !important`
                    : undefined
                })}
                onClick={(event) => handleClick(event, file)}>
                <Flex direction="row" justify="space-between">
                  <Flex direction="row" align="center" maw={500}>
                    {getFileIcon(file)}
                    <Text ml={5} truncate>
                      {file.name}
                    </Text>
                  </Flex>
                  {file.mimeType === GoogleDriveMimeType.FOLDER && (
                    <ActionIcon
                      size={20}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDoubleClick(file);
                      }}>
                      <IconCircleChevronRight />
                    </ActionIcon>
                  )}
                </Flex>
              </Paper>
            ))}
          </ScrollArea>
        )}
      </Box>
    </Box>
  ) : (
    <Box ml="auto" mr="auto" w="90%" h="100%" py={70}>
      <GoogleDriveAccountHandler
        h={650}
        onAccounClick={(account) => {
          setCurrentFilePath([{ ...GOOGLE_DRIVE_ROOT, googleDriveAccountId: account._id }]);
          setSelectedGoogleDriveAccount(account);
        }}
      />
    </Box>
  );
}
